import { useMemo } from 'react';

import type { ApiListingMiniExtra } from '#/api/types/ApiListingMini';
import { DropdownChevron } from '#/components/Dropdown/Chevron/DropdownChevron';
import type { IconName } from '#/components/Icon/IconName';
import { createTab } from '#/components/Tabs/createTab';
import { TabsHorizontal } from '#/components/Tabs/Horizontal/TabsHorizontal';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { ApiOrg } from '#/modules/listing/orgs/api/types/ApiOrg';
import type { ViewOrgPageTabSlug } from '#/modules/listing/orgs/pages/view/ViewOrgPageTabSlug';
import type { DatetimeString } from '#/types/TypedDateString';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import {
  OrgListingsTabsDesktop,
  OrgListingsTabsMobile,
  OrgListingsTabsMobileDetails,
  OrgListingsTabsMobileSummary,
} from './OrgListingsTabs.styled';
import { OrgListingsTabsBody } from './OrgListingsTabsBody';
import { OrgListingsTabsLabel } from './OrgListingsTabsLabel';

type Props = {
  org: ApiOrg;
  tabSearchParam: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT' | null;
  now: DatetimeString;
  trackMobileOpen: string;
  trackDesktopOpen: string;
  trackShowMore: string;
  trackClickedListing: string;
};

export function OrgListingsTabs({
  org,
  tabSearchParam,
  now,
  trackMobileOpen,
  trackDesktopOpen,
  trackShowMore,
  trackClickedListing,
}: Props) {
  const { i18n, locale } = useI18n();

  const listingTabsData = useMemo(
    () =>
      (
        [
          {
            name: 'JOB',
            listings: org.jobs,
            total: org.jobCount,
            icon: 'briefcase',
            label: i18n('Jobs'),
          },
          {
            name: 'INTERNSHIP',
            listings: org.internships,
            total: org.internshipCount,
            icon: 'sign-path',
            label: i18n('Internships'),
          },
          {
            name: 'VOLOP',
            listings: org.volops,
            total: org.volopCount,
            icon: 'hand',
            label: i18n('Volunteering'),
          },
          {
            name: 'EVENT',
            listings: org.events,
            total: org.eventCount,
            icon: 'star-calendar',
            label: i18n('Events'),
          },
        ] satisfies {
          name: ViewOrgPageTabSlug;
          listings: ApiListingMiniExtra[];
          total: number;
          icon: IconName;
          label: string;
        }[]
      ).filter((item) => item.total > 0),
    [org, i18n],
  );

  const selectedTabData =
    listingTabsData.find((item) => item.name === tabSearchParam) ??
    listingTabsData[0];

  const tabs = listingTabsData.map((item) =>
    createTab({
      prefix: 'org-listings',
      value: item.name,
      href: org.url[locale] + `?tab=${item.name}`,
      preventScrollReset: true,
      label: (
        <OrgListingsTabsLabel
          count={item.total}
          icon={item.icon}
          selected={selectedTabData.name === item.name}
        >
          {item.label}
        </OrgListingsTabsLabel>
      ),
    }),
  );

  return (
    <>
      <OrgListingsTabsDesktop>
        <TabsHorizontal
          tabs={tabs}
          value={selectedTabData.name}
          onSelectTab={(value) => {
            trackClicked(trackDesktopOpen, { listing_type: value });
          }}
          compact
        />

        <OrgListingsTabsBody
          listingCount={selectedTabData.total}
          listingType={selectedTabData.name}
          listings={selectedTabData.listings}
          orgId={org.id}
          now={now}
          trackShowMore={trackShowMore}
          trackClickedListing={trackClickedListing}
        />
      </OrgListingsTabsDesktop>

      <OrgListingsTabsMobile>
        {listingTabsData.map((item) => (
          <OrgListingsTabsMobileDetails
            key={item.name}
            defaultOpen={selectedTabData.name === item.name}
            trackClickedOpen={{
              name: trackMobileOpen,
              properties: { listing_type: item.name },
            }}
          >
            <OrgListingsTabsMobileSummary>
              <OrgListingsTabsLabel count={item.total} icon={item.icon}>
                {item.label}
              </OrgListingsTabsLabel>

              <DropdownChevron size={24} />
            </OrgListingsTabsMobileSummary>

            <OrgListingsTabsBody
              listingCount={item.total}
              listingType={item.name}
              listings={item.listings}
              orgId={org.id}
              now={now}
              trackShowMore={trackShowMore}
              trackClickedListing={trackClickedListing}
            />
          </OrgListingsTabsMobileDetails>
        ))}
      </OrgListingsTabsMobile>
    </>
  );
}
